import { isOrganizerHost } from '../config/host';

interface InstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export interface InstallSnapshot {
  canPrompt: boolean;
  installed: boolean;
  standalone: boolean;
}

export type InstallResult = 'accepted' | 'dismissed' | 'error' | 'unavailable';

const listeners = new Set<() => void>();
let promptEvent: InstallPromptEvent | null = null;
let pending = false;
let installed = false;
let displayMode: MediaQueryList | null = null;
let stopListening: (() => void) | null = null;

function isStandalone(): boolean {
  return Boolean(displayMode?.matches || (navigator as Navigator & { standalone?: boolean }).standalone);
}

let snapshot: InstallSnapshot = { canPrompt: false, installed: false, standalone: false };

function publish(): void {
  const next: InstallSnapshot = {
    canPrompt: promptEvent !== null && !pending,
    installed,
    standalone: isStandalone(),
  };
  if (
    next.canPrompt === snapshot.canPrompt &&
    next.installed === snapshot.installed &&
    next.standalone === snapshot.standalone
  ) return;
  snapshot = next;
  listeners.forEach((listener) => listener());
}

export function getInstallSnapshot(): InstallSnapshot {
  return snapshot;
}

export function subscribeInstall(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function initializePwaInstall(): () => void {
  if (isOrganizerHost()) return () => {};
  if (stopListening) return stopListening;

  displayMode = window.matchMedia?.('(display-mode: standalone)') ?? null;
  const onPrompt = (event: Event) => {
    if (isStandalone() || installed) return;
    event.preventDefault();
    promptEvent = event as InstallPromptEvent;
    publish();
  };
  const onInstalled = () => {
    installed = true;
    promptEvent = null;
    publish();
  };
  const onDisplayChange = () => publish();
  window.addEventListener('beforeinstallprompt', onPrompt);
  window.addEventListener('appinstalled', onInstalled);
  if (displayMode?.addEventListener) {
    displayMode.addEventListener('change', onDisplayChange);
  } else {
    displayMode?.addListener(onDisplayChange);
  }
  publish();

  const dispose = () => {
    window.removeEventListener('beforeinstallprompt', onPrompt);
    window.removeEventListener('appinstalled', onInstalled);
    if (displayMode?.removeEventListener) {
      displayMode.removeEventListener('change', onDisplayChange);
    } else {
      displayMode?.removeListener(onDisplayChange);
    }
    displayMode = null;
    promptEvent = null;
    pending = false;
    installed = false;
    stopListening = null;
    publish();
  };
  stopListening = dispose;
  return dispose;
}

export async function requestPwaInstall(): Promise<InstallResult> {
  // Consume synchronously in the user's click, before any awaited work or second click.
  if (!promptEvent || pending || installed || isStandalone()) return 'unavailable';
  const event = promptEvent;
  promptEvent = null;
  pending = true;
  publish();
  try {
    await event.prompt();
    return (await event.userChoice).outcome;
  } catch {
    return 'error';
  } finally {
    pending = false;
    publish();
  }
}
